import { z } from 'zod';

/**
 * Registry of metric keys the dashboard stats endpoint understands.
 * Adding a new key here is the single source of truth — the request
 * schema, response shape, and tile registry all derive from this.
 */
export const DASHBOARD_METRIC_KEYS = [
  'open_tasks',
  'completed_tasks',
  'completion_rate',
  'tasks_by_label',
  'tasks_by_assignee',
  'open_tasks_by_age',
  'referrals_count',
  'active_users_count',
  'throughput_trend',
  'time_to_resolution_by_label',
  // Outbound voice-call metrics. Disposition stats come from
  // `interactions.outcome_category`; pipeline stats from `interactions.status`.
  // All scoped server-side to channel='voice' AND direction='outbound'.
  'outbound_calls',
  'outbound_scheduled',
  'outbound_voicemails',
  'outbound_not_answered',
  'outbound_reached',
  'outbound_booking_rate',
  'outbound_outcomes',
  'outbound_call_trend',
  'outbound_efficiency',
  'outbound_pending',
  'outbound_in_progress',
] as const;

export const dashboardMetricKeySchema = z.enum(DASHBOARD_METRIC_KEYS);
export type DashboardMetricKey = z.infer<typeof dashboardMetricKeySchema>;

// =============================================================================
// Request
// =============================================================================

export const dashboardStatsRequestSchema = z
  .object({
    metrics: z.array(dashboardMetricKeySchema).min(1).max(20),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    /**
     * Optional per-metric params, keyed by metric key. Each entry is
     * forwarded to the matching fetcher's `paramsSchema` for
     * validation. Metrics whose fetcher has no `paramsSchema` ignore
     * this slot. No fetcher uses params today; the field exists so
     * future per-tile filters (assignee, label, status) don't require
     * a request-shape break.
     */
    metricParams: z.record(dashboardMetricKeySchema, z.unknown()).optional(),
  })
  .strict();

export type DashboardStatsRequest = z.infer<typeof dashboardStatsRequestSchema>;

// =============================================================================
// Per-metric response shapes
// =============================================================================

export const tasksByLabelItemSchema = z.object({
  /** The label's machine slug (matches `classification_labels.name`). */
  labelSlug: z.string(),
  /** Display name from `classification_labels.display_name`, falls back to the slug. */
  labelName: z.string(),
  openCount: z.number().int().nonnegative(),
  closedCount: z.number().int().nonnegative(),
});
export type TasksByLabelItem = z.infer<typeof tasksByLabelItemSchema>;

/** Discriminator for assignee rows — a row is either a user or a team. */
export const assigneeTypeSchema = z.enum(['user', 'team']);
export type AssigneeRowType = z.infer<typeof assigneeTypeSchema>;

export const tasksByAssigneeItemSchema = z.object({
  /** UUID of the user OR team — disambiguated by `assigneeType`. */
  assigneeId: z.string().uuid(),
  /** Display name (user's full name, or team name). */
  assigneeName: z.string(),
  assigneeType: assigneeTypeSchema,
  openCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  /** 0..1 ratio (frontend formats to %). */
  completionRate: z.number().min(0).max(1),
  /**
   * Average age in days of currently-open tasks for this assignee.
   * Open-only — closed tasks are excluded since their resolution time
   * is captured by other metrics. Null when the assignee has no open
   * tasks (avoids dividing by zero in the SQL).
   */
  avgAgeDays: z.number().nonnegative().nullable(),
  /**
   * Counts of currently-open tasks per `AGING_HEATMAP_BUCKETS` bucket
   * (< 1 day / 1-3 / 3-7 / 7-14). Sum equals `openCount`. Used by the
   * tile to surface "where each person's open work sits" inline,
   * which makes the standalone aging-heatmap tile redundant.
   */
  bucketCounts: z.array(z.number().int().nonnegative()),
});
export type TasksByAssigneeItem = z.infer<typeof tasksByAssigneeItemSchema>;

/**
 * One bucket in the open-task-age breakdown. `maxDays` is null for the
 * open-ended tail bucket ("30+ days"). The frontend uses minDays/maxDays
 * to compute the per-bucket drilldown URL — `dateFrom = today - maxDays`,
 * `dateTo = today - minDays` (so older tasks are dateFrom-bounded).
 */
export const openTaskAgeBucketSchema = z.object({
  label: z.string(),
  minDays: z.number().int().nonnegative(),
  maxDays: z.number().int().nonnegative().nullable(),
  count: z.number().int().nonnegative(),
});
export type OpenTaskAgeBucket = z.infer<typeof openTaskAgeBucketSchema>;

const periodSchema = z.object({ from: z.string(), to: z.string() });

/**
 * Daily {created, completed} counts for the throughput tile. The tile
 * renders as a Recharts area chart — `date` is ISO YYYY-MM-DD, used as
 * the X axis. Always densified server-side: every day in the window
 * has a row even if both counts are zero.
 */
export const throughputDayPointSchema = z.object({
  date: z.string(),
  created: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
});
export type ThroughputDayPoint = z.infer<typeof throughputDayPointSchema>;

/**
 * Per-label resolution stats. `medianHours`/`p90Hours` are computed
 * server-side from `completed_at - created_at` over completed tasks
 * in the window. `bucketCounts` matches `TIME_TO_RESOLUTION_BUCKETS`
 * exactly (8 entries, ordered) so the frontend can render an inline
 * histogram bar without re-bucketing.
 */
export const timeToResolutionByLabelItemSchema = z.object({
  labelSlug: z.string(),
  labelName: z.string(),
  /** Total completed tasks for this label in the window. */
  totalCompleted: z.number().int().nonnegative(),
  /** Median resolution time in hours. Null when totalCompleted = 0. */
  medianHours: z.number().nonnegative().nullable(),
  /** p90 resolution time in hours. Null when totalCompleted = 0. */
  p90Hours: z.number().nonnegative().nullable(),
  /** Count of tasks that took longer than the 2-day target. */
  overTwoDaysCount: z.number().int().nonnegative(),
  /** Counts per bucket, aligned to TIME_TO_RESOLUTION_BUCKETS order. */
  bucketCounts: z.array(z.number().int().nonnegative()),
});
export type TimeToResolutionByLabelItem = z.infer<typeof timeToResolutionByLabelItemSchema>;

/**
 * Canonical bucket boundaries for time-to-resolution. Hours-based
 * (resolution times come in below the day mark frequently for fax
 * triage). The frontend reads these to render axis labels.
 */
export const TIME_TO_RESOLUTION_BUCKETS = [
  { label: '<1h', minHours: 0, maxHours: 1 },
  { label: '1-4h', minHours: 1, maxHours: 4 },
  { label: '4-12h', minHours: 4, maxHours: 12 },
  { label: '12h-1d', minHours: 12, maxHours: 24 },
  { label: '1-2d', minHours: 24, maxHours: 48 },
  { label: '2-4d', minHours: 48, maxHours: 96 },
  { label: '4-7d', minHours: 96, maxHours: 168 },
  { label: '>7d', minHours: 168, maxHours: null },
] as const;

/**
 * Canonical age-bucket boundaries for currently-open tasks. Used by
 * both the `open_tasks_by_age` tile (renders all six buckets) and the
 * inline aging columns on `tasks_by_assignee` (uses the first four).
 *
 * `maxDays === null` is the open-ended tail (`30+ days`). Order
 * matters — the SQL iterates these to construct the CASE expression
 * and the frontend iterates them for axis labels and drilldown URLs.
 */
export const OPEN_TASK_AGE_BUCKETS = [
  { label: '< 1 day', minDays: 0, maxDays: 1 },
  { label: '1-3 days', minDays: 1, maxDays: 3 },
  { label: '3-7 days', minDays: 3, maxDays: 7 },
  { label: '7-14 days', minDays: 7, maxDays: 14 },
  { label: '14-30 days', minDays: 14, maxDays: 30 },
  { label: '30+ days', minDays: 30, maxDays: null },
] as const;

/**
 * Subset rendered by the inline aging columns on the
 * `tasks_by_assignee` tile. The 14-30 / 30+ tail is dropped at the
 * column level — too long-tail to read at narrow tile widths — but
 * the underlying counts come from the same `OPEN_TASK_AGE_BUCKETS`
 * SQL so the numbers reconcile across tiles.
 */
export const ASSIGNEE_AGING_BUCKET_COUNT = 4 as const;

/**
 * Optional prior-period count for a stat metric. Window length matches
 * the current period, shifted back by exactly one window. Null when
 * there's no signal (e.g. an org younger than the prior window —
 * showing "▲ 5" against a baseline of zero would mislead).
 */
const priorCountSchema = z.object({ priorCount: z.number().int().nonnegative().nullable() });

// =============================================================================
// Outbound call metric response shapes
// =============================================================================

/**
 * One disposition bucket in the outbound-call breakdown. `outcome` is the
 * raw `interactions.outcome_category` value (kept as a string, not a strict
 * enum, so a newer server disposition renders in an older client instead of
 * failing validation). Densified server-side: every known disposition has a
 * row even when its count is zero, so the composition tile is stable.
 */
export const outboundOutcomeCountSchema = z.object({
  outcome: z.string(),
  count: z.number().int().nonnegative(),
});
export type OutboundOutcomeCount = z.infer<typeof outboundOutcomeCountSchema>;

/**
 * Daily point for the outbound disposition trend chart. `date` is ISO
 * YYYY-MM-DD (UTC day). Densified server-side to one row per day in the
 * window so the X axis is gap-free (same contract as `throughput_trend`).
 * `calls` is the day's total; the rest are the headline dispositions the
 * multi-line chart plots.
 */
export const outboundCallTrendPointSchema = z.object({
  date: z.string(),
  calls: z.number().int().nonnegative(),
  scheduled: z.number().int().nonnegative(),
  voicemail: z.number().int().nonnegative(),
  noAnswer: z.number().int().nonnegative(),
});
export type OutboundCallTrendPoint = z.infer<typeof outboundCallTrendPointSchema>;

export const dashboardMetricsSchema = z
  .object({
    open_tasks: z
      .object({ count: z.number().int().nonnegative() })
      .merge(priorCountSchema)
      .optional(),
    completed_tasks: z
      .object({ count: z.number().int().nonnegative() })
      .merge(periodSchema)
      .merge(priorCountSchema)
      .optional(),
    completion_rate: z
      .object({
        rate: z.number().min(0).max(1),
        completed: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        /** Prior-period rate (0..1). Null when prior signal isn't available. */
        priorRate: z.number().min(0).max(1).nullable(),
      })
      .merge(periodSchema)
      .optional(),
    tasks_by_label: z.object({ items: z.array(tasksByLabelItemSchema) }).optional(),
    tasks_by_assignee: z.object({ items: z.array(tasksByAssigneeItemSchema) }).optional(),
    open_tasks_by_age: z.object({ items: z.array(openTaskAgeBucketSchema) }).optional(),
    referrals_count: z
      .object({ count: z.number().int().nonnegative() })
      .merge(periodSchema)
      .merge(priorCountSchema)
      .optional(),
    active_users_count: z
      .object({ count: z.number().int().nonnegative() })
      .merge(priorCountSchema)
      .optional(),
    throughput_trend: z
      .object({ items: z.array(throughputDayPointSchema) })
      .merge(periodSchema)
      .optional(),
    time_to_resolution_by_label: z
      .object({
        items: z.array(timeToResolutionByLabelItemSchema),
      })
      .merge(periodSchema)
      .optional(),

    // ─── Outbound call disposition stats (window-bound counts + ▲/▼) ───
    outbound_calls: z
      .object({ count: z.number().int().nonnegative() })
      .merge(periodSchema)
      .merge(priorCountSchema)
      .optional(),
    outbound_scheduled: z
      .object({ count: z.number().int().nonnegative() })
      .merge(periodSchema)
      .merge(priorCountSchema)
      .optional(),
    outbound_voicemails: z
      .object({ count: z.number().int().nonnegative() })
      .merge(periodSchema)
      .merge(priorCountSchema)
      .optional(),
    outbound_not_answered: z
      .object({ count: z.number().int().nonnegative() })
      .merge(periodSchema)
      .merge(priorCountSchema)
      .optional(),
    /** Reached-a-human count + connect rate (reached ÷ completed calls). */
    outbound_reached: z
      .object({
        count: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
        connectRate: z.number().min(0).max(1),
        priorRate: z.number().min(0).max(1).nullable(),
      })
      .merge(periodSchema)
      .optional(),
    /** Booking rate = scheduled ÷ reached (0..1). */
    outbound_booking_rate: z
      .object({
        rate: z.number().min(0).max(1),
        scheduled: z.number().int().nonnegative(),
        reached: z.number().int().nonnegative(),
        priorRate: z.number().min(0).max(1).nullable(),
      })
      .merge(periodSchema)
      .optional(),
    /** Full disposition breakdown for the composition tile. */
    outbound_outcomes: z
      .object({
        items: z.array(outboundOutcomeCountSchema),
        total: z.number().int().nonnegative(),
      })
      .merge(periodSchema)
      .optional(),
    /** Daily per-disposition series for the trend chart. */
    outbound_call_trend: z
      .object({ items: z.array(outboundCallTrendPointSchema) })
      .merge(periodSchema)
      .optional(),
    /** Efficiency roll-up: duration, cost, cost-per-booking, attempts. */
    outbound_efficiency: z
      .object({
        completedCalls: z.number().int().nonnegative(),
        avgDurationSeconds: z.number().nonnegative().nullable(),
        totalCost: z.number().nonnegative(),
        costPerBooking: z.number().nonnegative().nullable(),
        avgAttempts: z.number().nonnegative().nullable(),
      })
      .merge(periodSchema)
      .optional(),

    // ─── Pipeline stats (current stock, from `status`) ───
    outbound_pending: z
      .object({ count: z.number().int().nonnegative() })
      .merge(priorCountSchema)
      .optional(),
    outbound_in_progress: z.object({ count: z.number().int().nonnegative() }).optional(),
  })
  .strict();

/**
 * Per-metric error envelope. Surfaces in `errors[metricKey]` when a
 * single fetcher throws — partial-failure isolation. The frontend tile
 * for the failed key reads its envelope to render an error state with
 * the request id; sibling tiles that succeeded render normally.
 *
 * `code` is a stable token, never SQL fragments or stack traces (server
 * logs the real error). `requestId` lets the user quote a token in a
 * support ticket without bouncing through the network tab.
 */
export const dashboardMetricErrorSchema = z.object({
  code: z.string(),
  requestId: z.string().optional(),
});
export type DashboardMetricError = z.infer<typeof dashboardMetricErrorSchema>;

export const dashboardStatsResponseSchema = z.object({
  metrics: dashboardMetricsSchema,
  /** Present only when at least one metric failed. */
  errors: z.record(dashboardMetricKeySchema, dashboardMetricErrorSchema).optional(),
});
export type DashboardStatsResponse = z.infer<typeof dashboardStatsResponseSchema>;

// =============================================================================
// Tile catalog (frontend-only, but typed in shared so backend can validate
// tile.type values that map 1:1 to a metric)
// =============================================================================

/**
 * Built-in tile types. The frontend tile registry maps these to render
 * components; persisted layouts reference them by string. This list is
 * authoritative — server-side layout validation rejects unknown tile
 * types via a frontend-side check (we deliberately keep the server
 * loose so a newer client can add a tile type without a backend deploy
 * — unknown types just render as "unknown tile" in older clients).
 */
export const TILE_TYPES = [
  'open_tasks',
  'completed_tasks',
  'completion_rate',
  'tasks_by_label',
  'tasks_by_assignee',
  'open_tasks_by_age',
  'referrals_count',
  'active_users_count',
  'throughput_trend',
  'time_to_resolution_by_label',
  'outbound_calls',
  'outbound_scheduled',
  'outbound_voicemails',
  'outbound_not_answered',
  'outbound_reached',
  'outbound_booking_rate',
  'outbound_outcomes',
  'outbound_call_trend',
  'outbound_efficiency',
  'outbound_pending',
  'outbound_in_progress',
] as const;

export type TileType = (typeof TILE_TYPES)[number];
